import { useCallback, useEffect, useRef, useState } from 'react'
import { now, differenceInSeconds } from '../utils/date-service'

// ----------------------------------------------------------------------
const DEFAULT_DELAY = 1000
// ----------------------------------------------------------------------
/**
 * Gets seconds
 * @param {Date} expiry
 * @returns {number|number}
 */
function getSecondsFromExpiry(expiry) {
  const secondsDistance = differenceInSeconds(expiry, now())
  return secondsDistance > 0 ? secondsDistance : 0
}

/**
 *
 * @param {number} secs
 * @returns {{seconds: number, hours: number, minutes: number, days: number}}
 */
function getTimeFromSeconds(secs) {
  const totalSeconds = Math.ceil(secs)
  const days = Math.floor(totalSeconds / (60 * 60 * 24))
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60))
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
  const seconds = Math.floor(totalSeconds % 60)

  return {
    seconds,
    minutes,
    hours,
    days,
  }
}

function useInterval(callback, delay) {
  const callbackRef = useRef()

  // update callback function with current render callback that has access to latest props and state
  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!delay) {
      return () => {}
    }

    const interval = setInterval(() => {
      callbackRef.current && callbackRef.current()
    }, delay)
    return () => clearInterval(interval)
  }, [delay])
}

/**
 * useTimer Hook
 * @param {Date} expiryDate
 * @param {Function} onExpire
 */
const useTimer = ({ expiryDate, onExpire = () => {} }) => {
  const [seconds, setSeconds] = useState(getSecondsFromExpiry(expiryDate))
  const [isRunning, setIsRunning] = useState(true)

  const handleExpire = useCallback(() => {
    onExpire()
    setIsRunning(false)
  }, [onExpire])

  useInterval(
    () => {
      const secondsValue = getSecondsFromExpiry(expiryDate)
      setSeconds(secondsValue)
      if (secondsValue <= 0) {
        handleExpire()
      }
    },
    isRunning ? DEFAULT_DELAY : null,
  )

  return { ...getTimeFromSeconds(seconds), isRunning }
}

export default useTimer
